import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { CheckCheck, ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react'

export type PasoRecorrido = {
  titulo: string
  cuerpo: ReactNode
  /** Icono dentro del cuadro azul claro. Sin él la tarjeta va sin ilustración. */
  icono?: LucideIcon
  /**
   * Ruta de una imagen que sustituye al icono. Va sin el cuadro azul, porque la
   * imagen ya trae el suyo. Se pinta como decorativa —`alt` vacío—: lo que dice
   * el paso ya está en el título.
   */
  imagen?: string
  /**
   * Valor del `data-recorrido` del elemento a señalar. Si no se pone —o si el
   * elemento no está en pantalla— la tarjeta se centra y el fondo se oscurece
   * completo, que es como se ven el primero y el último paso.
   */
  ancla?: string
  /**
   * Muestra de apoyo, que se dibuja como una pieza aparte debajo de la tarjeta.
   * Es para los pasos que explican algo que vive dentro de un modal: en vez de
   * abrir el modal de verdad se enseña el pedazo que importa, y la tarjeta
   * queda centrada arriba señalándolo.
   */
  panel?: ReactNode
  /**
   * Corre antes de medir el paso: cambiar de pestaña, marcar una casilla. La
   * medición espera a que el elemento exista y ya tenga tamaño.
   */
  preparar?: () => void
}

/** Lo que separa la tarjeta del elemento que señala. */
const AIRE = 14
/** Cuánto se agranda el hueco alrededor del elemento resaltado. */
const HOLGURA = 6
const ANCHO_TARJETA = 500
const OSCURECIDO = 'rgba(15, 23, 42, 0.6)'

type Posicion = { top: number; left: number; lado: 'arriba' | 'abajo'; flecha: number }

const mismoRecuadro = (a: DOMRect | null, b: DOMRect) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height

/**
 * Recorrido guiado: oscurece la pantalla, deja ver el elemento del paso y
 * explica qué hace en una tarjeta que lo señala.
 *
 * El oscurecido no es un fondo sobre el elemento resaltado, es una sombra
 * enorme alrededor del hueco (`box-shadow` con extensión de 9999px): así el
 * elemento se ve tal como está en la pantalla, sin recortarlo ni redibujarlo.
 *
 * La capa entera captura los clics, así que durante el recorrido no se puede
 * tocar nada de la pantalla de abajo, ni siquiera lo que está iluminado. Es a
 * propósito: el recorrido explica, no deja operar a medias.
 */
export function Recorrido({
  pasos,
  onCerrar,
}: {
  pasos: PasoRecorrido[]
  onCerrar: () => void
}) {
  const [indice, setIndice] = useState(0)
  const [recuadro, setRecuadro] = useState<DOMRect | null>(null)
  const [tarjeta, setTarjeta] = useState<HTMLDivElement | null>(null)
  const [posicion, setPosicion] = useState<Posicion | null>(null)

  const paso = pasos[indice]
  const primero = indice === 0
  const ultimo = indice === pasos.length - 1

  // Se trabaja con el nombre del ancla y no con el objeto del paso: quien monta
  // el recorrido puede rearmar el arreglo en cada render, y si los efectos
  // dependieran de la identidad del objeto se reiniciarían solos.
  const ancla = paso?.ancla
  const prepararRef = useRef(paso?.preparar)
  prepararRef.current = paso?.preparar

  /**
   * Mide el elemento del paso. Devuelve si quedó medido.
   *
   * Una medida sin tamaño no cuenta: un elemento recién montado puede existir
   * con 0x0 —la fila ya está en el DOM pero la tabla todavía no acomoda—, y
   * aceptarla deja un hueco de 0x0, o sea la pantalla oscurecida completa y
   * justo el elemento que se quería señalar tapado. Ese era el bug del paso que
   * a veces no abría la ventanita.
   */
  const medir = useCallback(() => {
    if (!ancla) {
      setRecuadro(null)
      return true
    }
    const nodo = document.querySelector(`[data-recorrido="${ancla}"]`)
    if (!nodo) return false

    if (nodo.getBoundingClientRect().width === 0) return false

    nodo.scrollIntoView({ block: 'center', inline: 'nearest' })
    const rect = nodo.getBoundingClientRect()
    setRecuadro((previo) => (mismoRecuadro(previo, rect) ? previo : rect))
    return true
  }, [ancla])

  // Preparar y medir. Varios pasos señalan algo que todavía no existe cuando
  // arrancan: `preparar` cambia la pestaña o marca una casilla, y el elemento
  // aparece con el render siguiente.
  //
  // Se escucha el DOM en vez de reintentar contra el reloj: un MutationObserver
  // avisa en cuanto el elemento entra, sin importar cuánto tarde el render, y
  // los temporizadores y los cuadros de animación se frenan en una pestaña en
  // segundo plano. El observador se queda conectado todo el paso y no se corta
  // en la primera medida: la tabla sigue acomodándose después —se le agregan
  // filas, cambia el alto— y el hueco tiene que seguir al elemento.
  useEffect(() => {
    // Se limpia primero: si no, mientras aparece el elemento del paso nuevo se
    // quedaría iluminado el del paso anterior.
    setRecuadro(null)
    prepararRef.current?.()
    medir()

    const observador = new MutationObserver(() => medir())
    observador.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })

    return () => observador.disconnect()
  }, [indice, medir])

  // La página de abajo puede moverse: al hacer scroll o al cambiar el tamaño el
  // hueco tiene que seguir al elemento.
  useEffect(() => {
    if (!ancla) return
    const alMover = () => medir()
    window.addEventListener('resize', alMover)
    window.addEventListener('scroll', alMover, true)
    return () => {
      window.removeEventListener('resize', alMover)
      window.removeEventListener('scroll', alMover, true)
    }
  }, [ancla, medir])

  // Con el elemento medido y la tarjeta ya pintada se decide de qué lado cabe.
  useEffect(() => {
    if (!tarjeta || !recuadro) {
      setPosicion(null)
      return
    }
    const alto = tarjeta.offsetHeight
    const ancho = tarjeta.offsetWidth

    const cabeAbajo = recuadro.bottom + AIRE + alto + 16 <= window.innerHeight
    const top = cabeAbajo ? recuadro.bottom + AIRE : Math.max(16, recuadro.top - AIRE - alto)

    const centro = recuadro.left + recuadro.width / 2
    const left = Math.min(
      Math.max(16, centro - ancho / 2),
      Math.max(16, window.innerWidth - ancho - 16),
    )

    setPosicion({
      top,
      left,
      lado: cabeAbajo ? 'abajo' : 'arriba',
      // La flecha apunta al centro del elemento, sin salirse de la tarjeta.
      flecha: Math.min(Math.max(centro - left, 20), ancho - 20),
    })
  }, [recuadro, tarjeta, indice])

  const siguiente = () => (ultimo ? onCerrar() : setIndice((i) => i + 1))
  const anterior = () => setIndice((i) => Math.max(0, i - 1))

  // Escape cierra y las flechas navegan: es un recorrido, no un formulario.
  //
  // Va sin arreglo de dependencias a propósito: el listener tiene que ver el
  // paso actual, y este componente se pinta pocas veces, así que reenganchar
  // en cada render sale más barato que memorizar tres callbacks.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar()
      if (e.key === 'ArrowRight') siguiente()
      if (e.key === 'ArrowLeft') anterior()
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  })

  if (!paso) return null

  const Icono = paso.icono
  const etiquetaAccesible = `Recorrido, paso ${indice + 1} de ${pasos.length}`

  const contenido = (
    <>
      <div className="relative flex items-center justify-between gap-4">
        <span className="text-xs font-medium text-[#315FB9]">
          Paso {indice + 1} de {pasos.length}
        </span>
        {/* Medidas del diseno: la fila mide 72x6 con 3px de hueco, o sea
            puntos de 6 y el activo de 18. */}
        <div className="flex items-center gap-[3px]">
          {pasos.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === indice ? 'w-[18px] bg-[#477FFA]' : 'w-1.5 bg-[#D8E2F8]'
              }`}
            />
          ))}
        </div>
      </div>

      {paso.imagen ? (
        <img src={paso.imagen} alt="" className="mt-5 mx-auto h-[78px] w-[78px]" />
      ) : (
        Icono && (
          <div className="mt-5 mx-auto w-[78px] h-[78px] rounded-[22px] bg-[#E4ECFB] flex items-center justify-center">
            <Icono className="w-9 h-9 text-[#315FB9]" strokeWidth={1.75} />
          </div>
        )
      )}

      <h2 className="mt-5 text-lg font-bold text-gray-900">{paso.titulo}</h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{paso.cuerpo}</p>

      <div className="mt-6 flex items-center justify-between gap-3">
        {/* En el último paso ya no hay nada que saltarse. */}
        {ultimo ? (
          <button
            onClick={anterior}
            className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            Anterior
          </button>
        ) : (
          <button
            onClick={onCerrar}
            className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            Saltar
          </button>
        )}

        <div className="flex items-center gap-3">
          {!primero && !ultimo && (
            <button
              onClick={anterior}
              className={estiloDeBoton('primario')}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
          )}
          <button
            onClick={siguiente}
            className={estiloDeBoton('primario')}
          >
            {ultimo ? 'Entendido' : 'Siguiente'}
            {ultimo ? <CheckCheck className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </>
  )

  // Paso con muestra de apoyo: la tarjeta va centrada y la muestra debajo, como
  // pieza aparte. Aquí no hay hueco que iluminar, porque lo que se enseña lo
  // dibuja el propio recorrido y no la pantalla de abajo.
  if (paso.panel) {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center px-4"
        style={{ backgroundColor: OSCURECIDO }}
        role="dialog"
        aria-modal="true"
        aria-label={etiquetaAccesible}
      >
        <div className="flex w-full max-w-[500px] flex-col items-center">
          <div className="relative w-full rounded-xl bg-white p-6 shadow-xl">
            {contenido}
            <div className="absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 bg-white" />
          </div>
          <div className="mt-5 w-full rounded-xl bg-white p-4 shadow-xl">{paso.panel}</div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[100]"
      // Sin ancla el oscurecido va aquí; con ancla lo pone la sombra del hueco.
      style={recuadro ? undefined : { backgroundColor: OSCURECIDO }}
      role="dialog"
      aria-modal="true"
      aria-label={etiquetaAccesible}
    >
      {/* El hueco iluminado */}
      {recuadro && (
        <div
          className="absolute rounded-lg pointer-events-none transition-all duration-200"
          style={{
            top: recuadro.top - HOLGURA,
            left: recuadro.left - HOLGURA,
            width: recuadro.width + HOLGURA * 2,
            height: recuadro.height + HOLGURA * 2,
            boxShadow: `0 0 0 9999px ${OSCURECIDO}`,
          }}
        />
      )}

      {/* La tarjeta */}
      <div
        ref={setTarjeta}
        className={`absolute bg-white rounded-xl shadow-xl p-6 ${
          posicion
            ? ''
            : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] max-w-[calc(100vw-32px)]'
        }`}
        style={
          posicion
            ? {
                top: posicion.top,
                left: posicion.left,
                width: ANCHO_TARJETA,
                maxWidth: 'calc(100vw - 32px)',
              }
            : undefined
        }
      >
        {/* La flecha que señala, solo cuando la tarjeta está anclada */}
        {posicion && (
          <div
            className="absolute w-4 h-4 bg-white rotate-45"
            style={{
              left: posicion.flecha - 8,
              [posicion.lado === 'abajo' ? 'top' : 'bottom']: -8,
            }}
          />
        )}

        {contenido}
      </div>
    </div>
  )
}

export default Recorrido
